import styled from 'styled-components'
import { Box, Stack } from '@mui/material'
import { theme } from '../../theme/theme'
import { Title2 } from '../styles/Typography'
import { MarkdownToReact } from '../markdown/MardownToReact'
import { useTranslation } from 'react-i18next'
import { useSelectFirstName, useSelectUserMarket } from '../../state/selectors'
import { LinkButton } from '../buttons/link/LinkButton'
import { SymptomTrend } from '@estercare/ester-shared'
import { isHealthCallsEnabled } from '../../utils/market'
import { StatusPill } from '../StatusPill'
import { useSymptomCounts } from '../../hooks/useSymptomCounts'
import { NewRecommendationsBanner } from './NewRecommendationsBanner'

interface SummaryCardProps {
  content: string
  isFollowUpHandled: boolean
}

export const SummaryCard = ({ content, isFollowUpHandled }: SummaryCardProps) => {
  const { t } = useTranslation()
  const firstName = useSelectFirstName()
  const userMarket = useSelectUserMarket()

  const greeting = firstName
    ? t('employee.yourHealth.helloGreeting', { firstName })
    : t('employee.yourHealth.healthPlanSummary')

  return (
    <Container>
      <Stack spacing={2} justifyContent="space-between">
        {isFollowUpHandled && <FollowUpIndicators />}
        <Box overflow="auto">
          <Greeting>{greeting}</Greeting>
          <MarkdownToReact markdown={content} />
        </Box>
        {isHealthCallsEnabled(userMarket) && (
          <ButtonWrapper>
            <LinkButton
              href="/portal/health-services/health-consultation"
              text={t('employee.screening.bookHealthCallButton')}
              buttonColor={theme.color.white}
              secondary
            />
          </ButtonWrapper>
        )}
      </Stack>
    </Container>
  )
}

const STATUS_PILLS = [
  { trend: SymptomTrend.Improving, key: 'improved' as const },
  { trend: SymptomTrend.Stable, key: 'stable' as const },
  { trend: SymptomTrend.NeedsAttention, key: 'needs_attention' as const },
]

const FollowUpIndicators = () => {
  const symptomCounts = useSymptomCounts()

  return (
    <TagContainer>
      <NewRecommendationsBanner variant="summary" />
      <StatusPillGroup>
        {STATUS_PILLS.map(
          ({ trend, key }) =>
            symptomCounts[key] > 0 && <StatusPill key={key} status={trend} amount={symptomCounts[key]} />
        )}
      </StatusPillGroup>
    </TagContainer>
  )
}

const Container = styled.div`
  border-radius: ${theme.spacing.medium}px;
  padding: ${theme.spacing.large}px;
  background-color: ${theme.color.plum};
  color: ${theme.color.white};
  display: flex;
  gap: ${theme.spacing.large}px;
  max-height: 900px;

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    padding: ${theme.spacing.medium}px;
    flex-direction: column-reverse;
    max-height: none;
  }
`

const TagContainer = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: ${theme.spacing.small}px;
  padding-bottom: ${theme.spacing.mediumLarge}px;
`

const StatusPillGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.small}px;
`

const Greeting = styled(Title2)`
  color: ${theme.color.white};
  margin-bottom: ${theme.spacing.medium}px;
`

const ButtonWrapper = styled.div`
  width: fit-content;
  @media screen and (max-width: ${theme.breakpoint.large}px) {
    width: 100%;
  }
`
